import Icon from 'components/atoms/Icon';
import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { emptyDisplayValue } from 'constants/common';
import {
  COMPANY_LIFECYCLE_LABELS,
  companyLifecycleOptions,
} from 'constants/companyLifecycle';
import {
  CompanyLifecycle,
  CompanyPageCompanyFragment,
  CompanyPageCompanyFragmentDoc,
  useCompanyPageUpdateCompanyMutation,
  useUploadCompanyLogoMutation,
} from 'generated/graphql';
import { formatDate } from 'helpers/date';
import { useState } from 'react';
import {
  Button,
  EditFormWrapper,
  FileInputField,
  SelectFormField,
  TextInputField,
  TNotification,
  TSelectOption,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import CsmUserSelect from '../CsmUserSelect/CsmUserSelect';
import styles from './BaseCompanyDetails.module.scss';

export default function BaseCompanyDetails({
  company,
}: {
  company: CompanyPageCompanyFragment;
}) {
  const [isEditing, setIsEditing] = useState(false);

  const handleCancelClick = () => {
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  return isEditing ? (
    <BaseCompanyDetailsForm
      company={company}
      onCancelClick={handleCancelClick}
      onSave={handleSave}
    />
  ) : (
    <BaseCompanyDetailsView company={company} onEditClick={handleEditClick} />
  );
}

function BaseCompanyDetailsView({
  company,
  onEditClick,
}: {
  company: CompanyPageCompanyFragment;
  onEditClick: () => void;
}) {
  return (
    <div className={styles.view}>
      <div className={styles.header}>
        <div className={styles.companyLogo}>
          {company.logoUrl ? (
            <img
              className={styles.companyLogoImg}
              src={company.logoUrl}
              alt="company logo"
            />
          ) : (
            <Icon icon="empty_company" className={styles.emptyCompanyLogo} />
          )}
        </div>
        <h2 className={styles.companyName}>{company.name}</h2>
        <Button size="medium" variant="secondary" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        <InfoItem
          className={styles.copyItem}
          label="Company ID"
          value={company.id.toString()}
          hasCopyButton
          hasCopyButtonTooltip="Copy company ID"
        />
        <InfoItem
          label="Created"
          value={formatDate(
            new Date(parseInt(company.createdAt)).toISOString(),
            'dd MMM yyyy'
          )}
        />
        <InfoItem
          label="Company lifecycle"
          value={COMPANY_LIFECYCLE_LABELS[company.companyLifecycle]}
        />
        <InfoItem
          label="Customer Success Manager"
          value={
            company.csmUser
              ? `${company.csmUser.firstName} ${company.csmUser.lastName}`
              : emptyDisplayValue
          }
        />
      </div>
    </div>
  );
}

function BaseCompanyDetailsForm({
  company,
  onCancelClick,
  onSave,
}: {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
}) {
  const { handleMsgType } = useNotification();
  const [companyName, setCompanyName] = useState(company.name);
  const [companyNameError, setCompanyNameError] = useState('');
  const [companyLogo, setCompanyLogo] = useState<File | null>(null);
  const [companyLogoError, setCompanyLogoError] = useState('');
  const [companyLifecycle, setCompanyLifecycle] = useState(
    company.companyLifecycle
  );
  const [csmUser, setCsmUser] = useState<TSelectOption<number> | undefined>(
    company.csmUser
      ? {
          label: `${company.csmUser.firstName} ${company.csmUser.lastName}`,
          value: company.csmUser.id,
          labelNote: company.csmUser.email,
        }
      : undefined
  );

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();

  const [uploadCompanyLogoMutation] = useUploadCompanyLogoMutation({
    context: {
      isUpload: true,
    },
    update(cache, { data }) {
      const companyLogoUrl = data?.uploadCompanyLogo;
      if (companyLogoUrl) {
        const cacheCompanyData = cache.readFragment<CompanyPageCompanyFragment>(
          {
            id: `Company:${company.id}`,
            fragment: CompanyPageCompanyFragmentDoc,
          }
        );
        if (cacheCompanyData) {
          cache.writeFragment<CompanyPageCompanyFragment>({
            id: `Company:${company.id}`,
            fragment: CompanyPageCompanyFragmentDoc,
            data: {
              ...cacheCompanyData,
              logoUrl: companyLogoUrl,
            },
          });
        }
      }
    },
  });

  const handleChangeCompanyName = (value: string) => {
    setCompanyName(value);
    setCompanyNameError('');
  };

  const handleChangeCompanyLogo = (value: File | null) => {
    setCompanyLogo(value);
    setCompanyLogoError('');
  };

  const handleChangeCompanyLifecycle = (
    option: TSelectOption<CompanyLifecycle>
  ) => {
    if (option.value) {
      setCompanyLifecycle(option.value);
    }
  };

  const handleSave = async () => {
    if (companyName.trim() === '') {
      setCompanyNameError('Company name is required');
      return;
    }

    try {
      let updated = false;
      const csmUserId = csmUser?.value ?? null;
      const currentCsmUserId = company.csmUser?.id ?? null;
      if (
        companyName !== company.name ||
        companyLifecycle !== company.companyLifecycle ||
        csmUserId !== currentCsmUserId
      ) {
        editCompanyMutation({
          variables: {
            id: company.id,
            name: companyName,
            companyLifecycle,
            csmUserId,
          },
        });
        updated = true;
      }

      if (companyLogo) {
        uploadCompanyLogoMutation({
          variables: {
            id: company.id,
            file: companyLogo,
          },
        });
        updated = true;
      }

      if (updated) {
        handleMsgType({
          type: TNotification.success,
          message: 'Company details updated successfully',
          dismissable: true,
        });
        onSave();
      }
    } catch (_error) {
      handleMsgType({
        type: TNotification.error,
        message:
          'Something went wrong while updating company details. Please try again.',
        dismissable: true,
      });
    }
  };

  return (
    <EditFormWrapper
      onCancelClick={onCancelClick}
      onSaveClick={handleSave}
      title={company.name}
      formId="edit-company-details-form"
      hasResetAllButton={false}
    >
      <div className={styles.formRow}>
        <TextInputField
          id="company-name"
          name="name"
          label="Company name"
          value={companyName}
          onChange={handleChangeCompanyName}
          hasError={!!companyNameError}
          bottomText={companyNameError}
          placeholder="Company name"
        />
        <SelectFormField<CompanyLifecycle>
          id="company-lifecycle"
          name="companyLifecycle"
          label="Company lifecycle"
          value={companyLifecycleOptions.find(
            option => option.value === companyLifecycle
          )}
          onChange={handleChangeCompanyLifecycle}
          options={companyLifecycleOptions}
          hasClearIndicator={false}
          isSearchable={false}
        />
      </div>
      <CsmUserSelect value={csmUser} onChange={setCsmUser} />
      <FileInputField
        id="company-logo"
        name="logo"
        label="Company logo"
        value={companyLogo}
        onChange={handleChangeCompanyLogo}
        hasError={!!companyLogoError}
        bottomText={companyLogoError}
        placeholder="Company logo"
        acceptedFileTypes={['image/jpeg', 'image/png', 'image/svg+xml']}
      />
    </EditFormWrapper>
  );
}
